import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { parse, format } from 'date-fns';
import { FilterItems } from 'components/ReusableFilters/FilterItems';
import { LocalizableMessage, LocalizableMessageShape } from 'components/LocalizableMessage/LocalizableMessage';
import { TimeRange } from 'components/TimeRange/TimeRange';
import { SECONDS_TIMESTAMP_FORMAT } from 'constants/time-and-date';
import { Selector } from 'components/Selector/Selector';
import { TimeSlider } from './TimeSlider';

// The sliders represent time as 48 thirty-minute blocks. The range 9-46
// represents 0430 - 2300. The following functions map between the range
// slider values, and actual times.

export const getParsedTimeValues = selectedTimeRange => {
  if (selectedTimeRange) {
    const [start, end] = selectedTimeRange.split('-');
    return [parse(start, 'H:m', new Date()), parse(end, 'H:m', new Date())];
  }

  return [];
};

class TimeFilter extends Component {
  state = {
    isSlider: this.props.isSlider,
  };

  handleOnClear = () => {
    const { onClear, onSetIsEditing } = this.props;

    onClear();
    onSetIsEditing(false);
  };

  handleOnDone = () => {
    const { isOutOfSync, onSyncTimeFilter } = this.props;

    if (isOutOfSync) {
      onSyncTimeFilter();
    }

    this.handleToggleExpanded();
  };

  onHandlePresetSelected = selectedPresetId => {
    const { onTimeRangeSelected } = this.props;

    onTimeRangeSelected(selectedPresetId, {
      resetIsEditing: true,
    });
  };

  handleToggleSlider = () => {
    this.setState(({ isSlider }) => ({
      isSlider: !isSlider,
    }));
  };

  handleSliderChange = selectedTimeRange => {
    const { onTimeRangeSelected } = this.props;

    onTimeRangeSelected(selectedTimeRange, {
      excludeFromWriteActions: true,
    });
  };

  handleToggleExpanded = () => {
    const { onSetIsEditing, isEditing } = this.props;

    onSetIsEditing(!isEditing);
  };

  renderPresets() {
    const { defaultValue, presets, selectedTimeRange } = this.props;

    return (
      <ul className="presets p-">
        <FilterItems
          defaultItem={defaultValue}
          itemsById={presets}
          itemIdSelected={selectedTimeRange ? { [selectedTimeRange]: true } : {}}
          toggleItemId={this.onHandlePresetSelected}>
          {items =>
            items.map(({ description, id, name, onClick, selected }) => (
              // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
              <li
                key={id}
                className={classNames('presets__item', {
                  'is-selected': selected,
                })}
                onClick={() => {
                  if (!selected) {
                    onClick(id);
                  } else {
                    this.handleOnClear();
                  }
                }}>
                <LocalizableMessage message={name} tagName="strong" />
                <LocalizableMessage message={description} />
              </li>
            ))
          }
        </FilterItems>
      </ul>
    );
  }

  renderSelectedName() {
    const { isSelected, presetName, selectedTimeRange } = this.props;

    if (isSelected) {
      if (presetName) {
        return <LocalizableMessage message={presetName} />;
      }
      if (selectedTimeRange) {
        const [start, end] = getParsedTimeValues(selectedTimeRange);

        return (
          <TimeRange
            startTimestamp={format(start, SECONDS_TIMESTAMP_FORMAT)}
            endTimestamp={format(end, SECONDS_TIMESTAMP_FORMAT)}
          />
        );
      }
    }

    return <FormattedMessage id="filter.time" defaultMessage="Any time" />;
  }

  renderSlider() {
    const { selectedTimeRange } = this.props;

    const [start, end] = getParsedTimeValues(selectedTimeRange);

    return <TimeSlider end={end} onChange={this.handleSliderChange} start={start} value={selectedTimeRange} />;
  }

  renderToggleMessage() {
    if (this.state.isSlider) {
      return (
        <a className="filter-controls__toggle" onClick={this.handleToggleSlider}>
          <FormattedMessage id="filter.time.presets" defaultMessage="Time presets" />
        </a>
      );
    }

    return (
      <a className="filter-controls__toggle" onClick={this.handleToggleSlider}>
        <FormattedMessage id="filter.time.slider" defaultMessage="Time slider" />
      </a>
    );
  }

  render() {
    const { isMobile, isSelected, isEditing } = this.props;

    const { isSlider } = this.state;

    return (
      <Selector isMobile={isMobile} isSelected={isSelected} isOpen={isEditing} onToggle={this.handleToggleExpanded}>
        {({ Controls, DisclosureArrow, Panel, Selection }) => (
          <div className="unified-filters__filter">
            <Selection>
              {this.renderSelectedName()}
              <DisclosureArrow />
            </Selection>
            <Panel onClose={this.handleOnDone} className="unified-filters__filter__panel--date">
              <div className="unified-filters__content">
                {isSlider ? this.renderSlider() : this.renderPresets()}
                <Controls disableClearControl={!isSelected} onDone={this.handleOnDone} onClear={this.handleOnClear}>
                  {this.renderToggleMessage()}
                </Controls>
              </div>
            </Panel>
          </div>
        )}
      </Selector>
    );
  }
}

TimeFilter.propTypes = {
  isMobile: PropTypes.bool,
  isSelected: PropTypes.bool,
  isSlider: PropTypes.bool,
  onClear: PropTypes.func.isRequired,
  onTimeRangeSelected: PropTypes.func.isRequired,
  onSyncTimeFilter: PropTypes.func.isRequired,
  defaultValue: PropTypes.string,
  presets: PropTypes.objectOf(
    PropTypes.shape({
      description: LocalizableMessageShape,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: LocalizableMessageShape,
    })
  ),
  presetName: LocalizableMessageShape,
  selectedTimeRange: PropTypes.string,
  isOutOfSync: PropTypes.bool.isRequired,
  isEditing: PropTypes.bool.isRequired,
  onSetIsEditing: PropTypes.func.isRequired,
};

export { TimeFilter };
